import {useEffect, useRef} from 'react';

export interface FissionProposalSavedMessage {
  url: string;
  password?: string;
  label?: string;
}

export interface FissionResourceMessage {
  resourceId: string;
  companyName?: string;
  label?: string;
}

export interface FissionMessageHandlers {
  /** External Quick Proposal link (`proposal-saved`: url + optional password). */
  onProposalSaved?: (message: FissionProposalSavedMessage) => void;
  /** Informed Analysis resource (`ia-run-created`: resourceId). */
  onIaRunCreated?: (message: FissionResourceMessage) => void;
  /** Infinity-hosted Quick Proposal resource (`qp-proposal-created`: resourceId). */
  onQpProposalCreated?: (message: FissionResourceMessage) => void;
}

/**
 * Subscribes to `postMessage` events emitted by the embedded Fission proposal
 * generator iframe and dispatches them to the supplied handlers.
 *
 * Security: a message is only processed when `event.origin` exactly matches
 * `expectedOrigin` — the Fission embed origin the backend returns alongside the
 * generator URL. When `expectedOrigin` is null/undefined (the URL query hasn't
 * resolved, or Fission isn't configured for the tenant) no listener is attached,
 * so a forged `proposal-saved` from an arbitrary iframe/window can't be
 * persisted with an attacker-controlled URL.
 */
export function useFissionMessage(
  expectedOrigin: string | null | undefined,
  handlers: FissionMessageHandlers,
): void {
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!expectedOrigin) return;

    const listener = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (!event.data || typeof event.data !== 'object') return;

      const data = event.data as {
        type?: string;
        url?: string;
        password?: string;
        label?: string;
        resourceId?: string;
        companyName?: string;
      };

      switch (data.type) {
        case 'proposal-saved':
          if (!data.url) return;
          handlersRef.current.onProposalSaved?.({
            url: data.url,
            password: data.password,
            label: data.label,
          });
          return;
        case 'ia-run-created':
          if (!data.resourceId) return;
          handlersRef.current.onIaRunCreated?.({
            resourceId: data.resourceId,
            companyName: data.companyName,
            label: data.label,
          });
          return;
        case 'qp-proposal-created':
          if (!data.resourceId) return;
          handlersRef.current.onQpProposalCreated?.({
            resourceId: data.resourceId,
            companyName: data.companyName,
            label: data.label,
          });
          return;
        default:
          return;
      }
    };

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [expectedOrigin]);
}
